<template>
  <v-row class="geomap ma-0 pa-0">
    <v-col cols="12" class="chart_style ma-0 pa-0">
      <!-- <div class="geomap-title">{{ chart_title }}</div> -->
      <div class="geomap-chart-body">
        <div
          :id="chart_id"
          :data-chart-ref="chart_ref"
          ref="mapContainer"
          class="geomap-map"
        ></div>
        <div v-if="province_data.length > 0" class="geomap-legend">
          <span>Low</span>
          <span class="geomap-legend__gradient"></span>
          <span>High</span>
        </div>
        <v-row
          v-if="loading"
          class="geomap-loader ma-0 pa-0"
          align="center"
          justify="center"
        >
          <v-progress-circular
            color="black"
            size="100"
            indeterminate
          ></v-progress-circular>
        </v-row>
      </div>
    </v-col>
  </v-row>
</template>

<script>
const LEAFLET_SCRIPT_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_STYLE_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const PROJ4_SCRIPT_URL = "https://cdnjs.cloudflare.com/ajax/libs/proj4js/2.9.2/proj4.js";
const STREET_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const STREET_TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const FSA_DATA_BASE_PATH = "/data/GeoData";
const LOW_DETAIL_FSA_DATA_BASE_PATH = `${FSA_DATA_BASE_PATH}/low_detail`;
const CANADA_CLUSTER_MAP_URLS = {
  national: [
    `${FSA_DATA_BASE_PATH}/canada_provinces_territories_national.geojson?v=statcan-province-v1`,
    `${FSA_DATA_BASE_PATH}/canada_provinces_territories.geojson`,
  ],
  detailed: [
    `${FSA_DATA_BASE_PATH}/canada_provinces_territories_detailed.geojson?v=statcan-province-v1`,
    `${FSA_DATA_BASE_PATH}/canada_provinces_territories.geojson`,
  ],
};
const DETAILED_CLUSTER_MIN_ZOOM = 5;
const MAP_MIN_ZOOM = 3;
const FSA_DETAIL_MIN_ZOOM = 6;
const TILE_MAX_NATIVE_ZOOM = 7;
const MAP_MAX_ZOOM = 10;
const MAP_MODE_NATIONAL_CLUSTER = "national";
const MAP_MODE_DETAILED_CLUSTER = "detailed";
const MAP_MODE_FSA = "fsa";
const CANADA_BOUNDS = [
  [40, -142],
  [84, -52],
];
const EPSG_STATCAN_LAMBERT =
  "+proj=lcc +lat_0=63.390675 +lon_0=-91.8666666666667 +lat_1=49 +lat_2=77 +x_0=6200000 +y_0=3000000 +datum=NAD83 +units=m +no_defs +type=crs";
const RENDER_DETAIL_SETTINGS = {
  high: {
    projectedTolerance: 75,
    wgs84Tolerance: 0.00075,
    showOnlyDataFsa: false,
    smoothFactor: 0.5,
  },
  medium: {
    projectedTolerance: 175,
    wgs84Tolerance: 0.00175,
    showOnlyDataFsa: false,
    smoothFactor: 0.8,
  },
  low: {
    projectedTolerance: 1200,
    wgs84Tolerance: 0.012,
    showOnlyDataFsa: true,
    smoothFactor: 1.2,
  },
};

const PROVINCE_FSA_STARTING_CODE_MAPPING = {
  ontario: ["K", "L", "M", "N", "P"],
  quebec: ["G", "H", "J"],
  british_columbia: ["V"],
  alberta: ["T"],
  saskatchewan: ["S"],
  manitoba: ["R"],
  new_brunswick: ["E"],
  nova_scotia: ["B"],
  prince_edward_island: ["C"],
  newfoundland_labrador: ["A"],
  yukon: ["Y"],
  northwest_territories: ["X"],
  nunavut: ["X"],
};
const AVAILABLE_PROVINCE_OPTIONS = [
  { text: "Ontario", value: "ontario" },
  { text: "Quebec", value: "quebec" },
  { text: "British Columbia", value: "british_columbia" },
  { text: "Alberta", value: "alberta" },
  { text: "Saskatchewan", value: "saskatchewan" },
  { text: "Manitoba", value: "manitoba" },
  { text: "New Brunswick", value: "new_brunswick" },
  { text: "Nova Scotia", value: "nova_scotia" },
  { text: "Prince Edward Island", value: "prince_edward_island" },
  { text: "Newfoundland and Labrador", value: "newfoundland_labrador" },
  { text: "Yukon", value: "yukon" },
  { text: "Northwest Territories", value: "northwest_territories" },
  { text: "Nunavut", value: "nunavut" },
];

let leafletLoader = null;
let proj4Loader = null;

export default {
  name: "GeoMapV2",
  props: ["chart_title", "chart_data", "chart_id", "chart_ref", "raw_data_stat"],
  data: () => ({
    option: {},
    loading: true,
    province_data: [],
    heatmap_colors_red_blue: [
      "#0000ff", "#0200fc", "#0500f9", "#0700f7", "#0a00f4", "#0c00f2", "#0f00ef", "#1200ec", "#1400ea", "#1700e7",
      "#1900e5", "#1c00e2", "#1e00e0", "#2100dd", "#2400da", "#2600d8", "#2900d5", "#2b00d3", "#2e00d0", "#3100cd",
      "#3300cb", "#3600c8", "#3800c6", "#3b00c3", "#3e00c0", "#4000be", "#4300bb", "#4500b9", "#4800b6", "#4b00b3",
      "#4d00b1", "#5000ae", "#5200ac", "#5500a9", "#5800a6", "#5a00a4", "#5d00a1", "#5f009f", "#62009c", "#650099",
      "#670097", "#6a0094", "#6d0091", "#6f008f", "#72008c", "#74008a", "#770087", "#7a0084", "#7c0082", "#7f007f",
      "#82007c", "#84007a", "#870077", "#890075", "#8c0072", "#8f006f", "#91006d", "#94006a", "#960068", "#990065",
      "#9c0062", "#9e0060", "#a1005d", "#a4005a", "#a60058", "#a90055", "#ab0053", "#ae0050", "#b1004d", "#b3004b",
      "#b60048", "#b80046", "#bb0043", "#be0040", "#c0003e", "#c3003b", "#cd0031", "#d0002e", "#d3002b", "#d50029",
      "#d80026", "#da0024", "#dd0021", "#e0001e", "#e2001c", "#e50019", "#e70017", "#ea0014", "#ec0012", "#ef000f",
      "#f2000c", "#f4000a", "#f70007", "#f90005", "#fc0002", "#ff0000",
    ],
    province_options: [],
    canadaClusterDataByMode: {},
    fsaDataByProvince: {},
    fsaGeoJsonItems: [],
    map: null,
    fsaLayer: null,
    provinceClusterLayer: null,
    provinceSummaryByPruid: {},
    vectorRenderer: null,
    renderDetailMode: "high",
    activeMapMode: null,
    lowDetailTooltip: null,
    lowDetailTooltipRecords: [],
    lowDetailTooltipFrame: null,
    lowDetailTooltipEvent: null,
    lowDetailHoveredFsaName: null,
    renderRequestId: 0,
  }),
  watch: {
    chart_data: {
      handler() {
        this.set_up_graph_options();
      },
      deep: true,
    },
    raw_data_stat() {
      this.set_up_graph_options();
    },
  },
  mounted() {
    this.set_up_graph_options();
  },
  beforeDestroy() {
    this.clearLowDetailTooltipHandlers();
    if (this.map) {
      this.map.off("zoomend", this.handleMapZoomEnd);
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    async set_up_graph_options() {
      const requestId = ++this.renderRequestId;
      this.loading = true;

      try {
        this.setProvinceOptions();
        this.province_data = this.getAllProvinceData();
        this.renderDetailMode = this.getRenderDetailMode();

        await this.loadLeaflet();
        await this.loadProj4();

        if (requestId !== this.renderRequestId) {
          return;
        }

        this.createMap();

        if (this.province_options.length === 0) {
          this.clearMapLayers();
          this.fitMapToCanada();
          return;
        }

        const [canadaClusterDataByMode, provinceGeoJsonList] = await Promise.all([
          this.loadCanadaClusterGeoJsonByMode(),
          Promise.all(
            this.province_options.map((province) => {
              return this.loadProvinceGeoJson(province.value, this.renderDetailMode);
            })
          ),
        ]);
        if (requestId !== this.renderRequestId) {
          return;
        }

        this.canadaClusterDataByMode = canadaClusterDataByMode;
        this.fsaGeoJsonItems = provinceGeoJsonList;
        this.provinceSummaryByPruid = this.buildProvinceSummaryByPruid(provinceGeoJsonList);
        this.activeMapMode = null;
        this.fitMapToDataBounds(this.getDataBoundsFromFsaGeoJsonItems(provinceGeoJsonList));
        this.updateVisibleMapLayer();
      } catch (error) {
        this.clearMapLayers();
        this.fitMapToCanada();
      } finally {
        if (requestId === this.renderRequestId) {
          this.invalidateMapSize();
          this.loading = false;
        }
      }
    },
    setProvinceOptions() {
      const fsaFirstLetters = new Set(
        (this.chart_data || [])
          .filter((item) => typeof item.name === "string" && item.name.length > 0)
          .map((item) => item.name[0].toUpperCase())
      );

      this.province_options = AVAILABLE_PROVINCE_OPTIONS.filter((province) => {
        const fsaPrefixes = PROVINCE_FSA_STARTING_CODE_MAPPING[province.value] || [];
        return fsaPrefixes.some((prefix) => fsaFirstLetters.has(prefix));
      });
    },
    getAllProvinceData() {
      return (this.chart_data || [])
        .filter((item) => {
          return item && typeof item.name === "string" && item.name.length > 0;
        })
        .map((item) => ({
          ...item,
          value: this.getSelectedFsaValue(item),
        }));
    },
    getSelectedFsaValue(item) {
      if (!item) {
        return 0;
      }

      const statKey = this.getSelectedStatKey();
      const selectedValue = Number(item[statKey]);
      if (Number.isFinite(selectedValue)) {
        return selectedValue;
      }

      const fallbackValue = Number(item.value);
      return Number.isFinite(fallbackValue) ? fallbackValue : 0;
    },
    getSelectedStatKey() {
      const availableStats = ["impressions", "reach", "frequency"];
      return availableStats.includes(this.raw_data_stat) ? this.raw_data_stat : "reach";
    },
    getSelectedStatLabel() {
      const statLabels = {
        impressions: "Impressions",
        reach: "Reach",
        frequency: "Frequency",
      };
      return statLabels[this.getSelectedStatKey()] || "Reach";
    },
    getRenderDetailMode() {
      const fsaCount = this.getFsaNameSet().size;
      const provinceCount = this.province_options.length;

      if (fsaCount >= 450 || provinceCount >= 6) {
        return "low";
      }

      if (fsaCount >= 160 || provinceCount >= 3) {
        return "medium";
      }

      return "high";
    },
    loadLeaflet() {
      if (window.L) {
        return Promise.resolve(window.L);
      }

      if (leafletLoader) {
        return leafletLoader;
      }

      leafletLoader = new Promise((resolve, reject) => {
        if (!document.querySelector(`link[href="${LEAFLET_STYLE_URL}"]`)) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = LEAFLET_STYLE_URL;
          document.head.appendChild(link);
        }

        const script = document.createElement("script");
        script.src = LEAFLET_SCRIPT_URL;
        script.onload = () => resolve(window.L);
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return leafletLoader;
    },
    loadProj4() {
      if (window.proj4) {
        this.defineProjectionSystems();
        return Promise.resolve(window.proj4);
      }

      if (proj4Loader) {
        return proj4Loader.then((proj4) => {
          this.defineProjectionSystems();
          return proj4;
        });
      }

      proj4Loader = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = PROJ4_SCRIPT_URL;
        script.onload = () => {
          this.defineProjectionSystems();
          resolve(window.proj4);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return proj4Loader;
    },
    defineProjectionSystems() {
      if (!window.proj4) {
        return;
      }

      window.proj4.defs("EPSG:3347", EPSG_STATCAN_LAMBERT);
      window.proj4.defs("EPSG:3348", EPSG_STATCAN_LAMBERT);
    },
    createMap() {
      if (this.map || !this.$refs.mapContainer || !window.L) {
        return;
      }

      this.map = window.L.map(this.$refs.mapContainer, {
        attributionControl: true,
        fadeAnimation: false,
        markerZoomAnimation: false,
        maxBounds: CANADA_BOUNDS,
        maxBoundsViscosity: 1,
        maxZoom: MAP_MAX_ZOOM,
        minZoom: MAP_MIN_ZOOM,
        preferCanvas: true,
        scrollWheelZoom: true,
        wheelPxPerZoomLevel: 90,
        zoomAnimation: false,
        zoomControl: true,
      });
      this.vectorRenderer = window.L.canvas({ padding: 0.2 });

      window.L.tileLayer(STREET_TILE_URL, {
        attribution: STREET_TILE_ATTRIBUTION,
        bounds: CANADA_BOUNDS,
        maxNativeZoom: TILE_MAX_NATIVE_ZOOM,
        maxZoom: MAP_MAX_ZOOM,
        noWrap: true,
        referrerPolicy: "strict-origin-when-cross-origin",
        updateWhenZooming: false,
      }).addTo(this.map);

      this.fsaLayer = window.L.featureGroup().addTo(this.map);
      this.provinceClusterLayer = window.L.featureGroup().addTo(this.map);
      this.map.on("zoomend", this.handleMapZoomEnd);
      this.fitMapToCanada();
    },
    handleMapZoomEnd() {
      this.updateVisibleMapLayer();
    },
    updateVisibleMapLayer() {
      if (!this.map || !this.provinceClusterLayer || !this.fsaLayer) {
        return;
      }

      const nextMapMode = this.getMapModeForZoom();
      if (this.activeMapMode === nextMapMode) {
        return;
      }

      this.activeMapMode = nextMapMode;
      if (nextMapMode === MAP_MODE_FSA) {
        this.clearProvinceClusterLayer();
        this.renderFsaLayer(this.fsaGeoJsonItems, false);
      } else {
        this.clearFsaLayer();
        this.renderProvinceClusterLayer();
      }
    },
    shouldShowFsaDetail() {
      return this.map && this.map.getZoom() >= FSA_DETAIL_MIN_ZOOM;
    },
    getMapModeForZoom() {
      if (this.shouldShowFsaDetail()) {
        return MAP_MODE_FSA;
      }

      if (this.map && this.map.getZoom() >= DETAILED_CLUSTER_MIN_ZOOM) {
        return MAP_MODE_DETAILED_CLUSTER;
      }

      return MAP_MODE_NATIONAL_CLUSTER;
    },
    isClusterMapMode() {
      return (
        this.activeMapMode === MAP_MODE_NATIONAL_CLUSTER ||
        this.activeMapMode === MAP_MODE_DETAILED_CLUSTER
      );
    },
    async loadCanadaClusterGeoJsonByMode() {
      const [nationalClusterData, detailedClusterData] = await Promise.all([
        this.loadCanadaClusterGeoJson(MAP_MODE_NATIONAL_CLUSTER),
        this.loadCanadaClusterGeoJson(MAP_MODE_DETAILED_CLUSTER),
      ]);

      return {
        [MAP_MODE_NATIONAL_CLUSTER]: nationalClusterData,
        [MAP_MODE_DETAILED_CLUSTER]: detailedClusterData,
      };
    },
    async loadCanadaClusterGeoJson(mapMode) {
      if (this.canadaClusterDataByMode[mapMode]) {
        return this.canadaClusterDataByMode[mapMode];
      }

      const clusterMapUrls = CANADA_CLUSTER_MAP_URLS[mapMode] || [];
      let geoJson = null;

      for (const clusterMapUrl of clusterMapUrls) {
        try {
          const response = await fetch(clusterMapUrl);
          if (!response.ok) {
            continue;
          }

          const clusterGeoJson = await response.json();
          if (clusterGeoJson && clusterGeoJson.type === "FeatureCollection") {
            geoJson = clusterGeoJson;
            break;
          }
        } catch {
          continue;
        }
      }

      if (!geoJson) {
        throw new Error("Unable to load Canada cluster map data");
      }

      const transformedGeoJson = this.transformGeoJsonToWgs84(geoJson);
      this.canadaClusterDataByMode = {
        ...this.canadaClusterDataByMode,
        [mapMode]: transformedGeoJson,
      };
      return transformedGeoJson;
    },
    async loadProvinceGeoJson(province, detailMode = "high") {
      const cacheKey = `${province}:${detailMode}`;
      if (this.fsaDataByProvince[cacheKey]) {
        return this.fsaDataByProvince[cacheKey];
      }

      const fsaBasePath = detailMode === "low"
        ? LOW_DETAIL_FSA_DATA_BASE_PATH
        : FSA_DATA_BASE_PATH;
      const response = await fetch(`${fsaBasePath}/${province}_fsa.json`);
      if (!response.ok) {
        throw new Error(`Unable to load FSA map data for ${province}`);
      }

      const geoJson = await response.json();
      const transformedGeoJson = this.prepareGeoJsonForRendering(geoJson, detailMode);
      this.fsaDataByProvince[cacheKey] = transformedGeoJson;
      return transformedGeoJson;
    },
    prepareGeoJsonForRendering(geoJson, detailMode) {
      const crsCode = this.getGeoJsonCrsCode(geoJson);
      if (detailMode === "low") {
        return this.transformGeoJsonToWgs84(geoJson);
      }

      const simplifyTolerance = this.getSimplifyTolerance(crsCode, detailMode);
      const simplifiedGeoJson = this.simplifyGeoJson(geoJson, simplifyTolerance);
      return this.transformGeoJsonToWgs84(simplifiedGeoJson);
    },
    getSimplifyTolerance(crsCode, detailMode) {
      const detailSettings = this.getRenderDetailSettings(detailMode);
      if (crsCode === "4269" || crsCode === "4326") {
        return detailSettings.wgs84Tolerance;
      }

      return detailSettings.projectedTolerance;
    },
    getRenderDetailSettings(detailMode = this.renderDetailMode) {
      return RENDER_DETAIL_SETTINGS[detailMode] || RENDER_DETAIL_SETTINGS.high;
    },
    simplifyGeoJson(geoJson, tolerance) {
      if (!Number.isFinite(tolerance) || tolerance <= 0) {
        return geoJson;
      }

      return {
        ...geoJson,
        features: (geoJson.features || []).map((feature) => ({
          ...feature,
          geometry: this.simplifyGeometry(feature.geometry, tolerance),
        })),
      };
    },
    simplifyGeometry(geometry, tolerance) {
      if (!geometry || !geometry.coordinates) {
        return geometry;
      }

      if (geometry.type === "Polygon") {
        return {
          ...geometry,
          coordinates: geometry.coordinates.map((ring) => this.simplifyRing(ring, tolerance)),
        };
      }

      if (geometry.type === "MultiPolygon") {
        return {
          ...geometry,
          coordinates: geometry.coordinates.map((polygon) => {
            return polygon.map((ring) => this.simplifyRing(ring, tolerance));
          }),
        };
      }

      if (geometry.type === "LineString") {
        return {
          ...geometry,
          coordinates: this.simplifyPath(geometry.coordinates, tolerance),
        };
      }

      if (geometry.type === "MultiLineString") {
        return {
          ...geometry,
          coordinates: geometry.coordinates.map((path) => this.simplifyPath(path, tolerance)),
        };
      }

      return geometry;
    },
    simplifyRing(ring, tolerance) {
      if (!Array.isArray(ring) || ring.length <= 4) {
        return ring;
      }

      const lastIndex = ring.length - 1;
      const isClosed = this.positionsEqual(ring[0], ring[lastIndex]);
      const simplifiedRing = isClosed
        ? this.simplifyClosedRing(ring.slice(0, lastIndex), tolerance)
        : this.simplifyPath(ring.slice(), tolerance);

      if (simplifiedRing.length < 3) {
        return ring;
      }

      const closedRing = isClosed ? [...simplifiedRing, simplifiedRing[0]] : simplifiedRing;
      return closedRing.length >= 4 ? closedRing : ring;
    },
    simplifyClosedRing(points, tolerance) {
      if (!Array.isArray(points) || points.length <= 3) {
        return points;
      }

      const splitIndex = this.getClosedRingSplitIndex(points);
      if (splitIndex <= 0) {
        return this.simplifyPath(points, tolerance);
      }

      const firstSegment = this.simplifyPath(points.slice(0, splitIndex + 1), tolerance);
      const secondSegment = this.simplifyPath(
        [...points.slice(splitIndex), points[0]],
        tolerance
      );
      const simplifiedRing = [
        ...firstSegment.slice(0, -1),
        ...secondSegment.slice(0, -1),
      ];

      return this.removeSequentialDuplicatePositions(simplifiedRing);
    },
    getClosedRingSplitIndex(points) {
      if (!Array.isArray(points) || points.length <= 3) {
        return 0;
      }

      const firstPoint = points[0];
      let maxSquareDistance = 0;
      let splitIndex = Math.floor(points.length / 2);

      for (let index = 1; index < points.length; index += 1) {
        const squareDistance = this.getSquareDistance(firstPoint, points[index]);
        if (squareDistance > maxSquareDistance) {
          maxSquareDistance = squareDistance;
          splitIndex = index;
        }
      }

      return splitIndex;
    },
    removeSequentialDuplicatePositions(points) {
      return points.filter((point, index) => {
        return index === 0 || !this.positionsEqual(point, points[index - 1]);
      });
    },
    simplifyPath(points, tolerance) {
      if (!Array.isArray(points) || points.length <= 2) {
        return points;
      }

      const sqTolerance = tolerance * tolerance;
      const radialSimplified = this.simplifyRadialDistance(points, sqTolerance);
      return this.simplifyDouglasPeucker(radialSimplified, sqTolerance);
    },
    simplifyRadialDistance(points, sqTolerance) {
      let previousPoint = points[0];
      const simplifiedPoints = [previousPoint];

      for (let index = 1; index < points.length; index += 1) {
        const point = points[index];
        if (this.getSquareDistance(point, previousPoint) > sqTolerance) {
          simplifiedPoints.push(point);
          previousPoint = point;
        }
      }

      const lastPoint = points[points.length - 1];
      if (!this.positionsEqual(previousPoint, lastPoint)) {
        simplifiedPoints.push(lastPoint);
      }

      return simplifiedPoints;
    },
    simplifyDouglasPeucker(points, sqTolerance) {
      const lastIndex = points.length - 1;
      const markers = new Uint8Array(points.length);
      const stack = [[0, lastIndex]];

      markers[0] = 1;
      markers[lastIndex] = 1;

      while (stack.length > 0) {
        const range = stack.pop();
        const firstIndex = range[0];
        const rangeLastIndex = range[1];
        let maxSquareDistance = 0;
        let indexWithMaxDistance = 0;

        for (let index = firstIndex + 1; index < rangeLastIndex; index += 1) {
          const squareDistance = this.getSquareSegmentDistance(
            points[index],
            points[firstIndex],
            points[rangeLastIndex]
          );

          if (squareDistance > maxSquareDistance) {
            indexWithMaxDistance = index;
            maxSquareDistance = squareDistance;
          }
        }

        if (maxSquareDistance > sqTolerance) {
          markers[indexWithMaxDistance] = 1;
          stack.push([firstIndex, indexWithMaxDistance]);
          stack.push([indexWithMaxDistance, rangeLastIndex]);
        }
      }

      return points.filter((point, index) => markers[index] === 1);
    },
    getSquareDistance(pointA, pointB) {
      const dx = pointA[0] - pointB[0];
      const dy = pointA[1] - pointB[1];
      return dx * dx + dy * dy;
    },
    getSquareSegmentDistance(point, segmentStart, segmentEnd) {
      let x = segmentStart[0];
      let y = segmentStart[1];
      let dx = segmentEnd[0] - x;
      let dy = segmentEnd[1] - y;

      if (dx !== 0 || dy !== 0) {
        const t = ((point[0] - x) * dx + (point[1] - y) * dy) / (dx * dx + dy * dy);

        if (t > 1) {
          x = segmentEnd[0];
          y = segmentEnd[1];
        } else if (t > 0) {
          x += dx * t;
          y += dy * t;
        }
      }

      dx = point[0] - x;
      dy = point[1] - y;
      return dx * dx + dy * dy;
    },
    positionsEqual(pointA, pointB) {
      return (
        Array.isArray(pointA) &&
        Array.isArray(pointB) &&
        pointA[0] === pointB[0] &&
        pointA[1] === pointB[1]
      );
    },
    transformGeoJsonToWgs84(geoJson) {
      const crsCode = this.getGeoJsonCrsCode(geoJson);
      if (crsCode === "4269" || crsCode === "4326") {
        return geoJson;
      }

      return {
        ...geoJson,
        crs: { type: "name", properties: { name: "urn:ogc:def:crs:EPSG::4326" } },
        features: (geoJson.features || []).map((feature) => ({
          ...feature,
          geometry: this.transformGeometryToWgs84(feature.geometry, crsCode),
        })),
      };
    },
    transformGeometryToWgs84(geometry, crsCode) {
      if (!geometry || !geometry.coordinates) {
        return geometry;
      }

      return {
        ...geometry,
        coordinates: this.transformCoordinatesToWgs84(geometry.coordinates, crsCode),
      };
    },
    transformCoordinatesToWgs84(coordinates, crsCode) {
      if (!Array.isArray(coordinates)) {
        return coordinates;
      }

      if (
        coordinates.length >= 2 &&
        typeof coordinates[0] === "number" &&
        typeof coordinates[1] === "number"
      ) {
        return this.transformPositionToWgs84(coordinates, crsCode);
      }

      return coordinates.map((coordinate) => this.transformCoordinatesToWgs84(coordinate, crsCode));
    },
    transformPositionToWgs84(position, crsCode) {
      if (!window.proj4 || !crsCode) {
        return position;
      }

      const transformedPosition = window.proj4(`EPSG:${crsCode}`, "EPSG:4326", [
        position[0],
        position[1],
      ]);
      return [transformedPosition[0], transformedPosition[1], ...position.slice(2)];
    },
    getGeoJsonCrsCode(geoJson) {
      const crsName = geoJson?.crs?.properties?.name || "";
      const epsgMatch = crsName.match(/EPSG::?(\d+)/i);
      return epsgMatch ? epsgMatch[1] : "4326";
    },
    renderProvinceClusterLayer() {
      const canadaClusterData = this.canadaClusterDataByMode[this.activeMapMode];
      if (!this.map || !this.provinceClusterLayer || !canadaClusterData || !window.L) {
        return;
      }

      this.clearProvinceClusterLayer();

      const provinceSummaries = this.provinceSummaryByPruid;
      const maxValue = this.getMaxProvinceClusterValue();

      window.L.geoJSON(canadaClusterData, {
        interactive: true,
        renderer: this.vectorRenderer,
        smoothFactor: 0,
        style: (feature) => this.getProvinceClusterStyle(feature, provinceSummaries, maxValue),
        onEachFeature: (feature, layer) => {
          const provinceName = this.getProvinceName(feature);
          const provinceSummary = provinceSummaries[String(feature.properties?.PRUID || "")];
          const value = provinceSummary ? provinceSummary.value : 0;

          layer.bindTooltip(
            `${this.escapeHtml(provinceName)}<br/>Average ${this.getSelectedStatLabel()}: ${this.formatStatValue(value)}`,
            {
              direction: "auto",
              opacity: 0.86,
              sticky: true,
            }
          );

          layer.on({
            mouseover: () => {
              layer.setStyle({
                color: "#111827",
                fillOpacity: provinceSummary ? 0.74 : 0.18,
                weight: 1.4,
              });
            },
            mouseout: () => {
              layer.setStyle(this.getProvinceClusterStyle(feature, provinceSummaries, maxValue));
            },
          });
        },
      }).addTo(this.provinceClusterLayer);
    },
    buildProvinceSummaryByPruid(geoJsonList) {
      const fsaValues = this.getFsaValueMap();
      const provinceSummaries = {};
      const geoJsonItems = Array.isArray(geoJsonList) ? geoJsonList : [geoJsonList];

      geoJsonItems.forEach((geoJson) => {
        (geoJson.features || []).forEach((feature) => {
          const fsaName = String(feature.properties?.name || "").toUpperCase();
          const provinceId = String(feature.properties?.PRUID || "");
          if (!provinceId || !Object.prototype.hasOwnProperty.call(fsaValues, fsaName)) {
            return;
          }

          if (!provinceSummaries[provinceId]) {
            provinceSummaries[provinceId] = {
              count: 0,
              total: 0,
              value: 0,
            };
          }

          provinceSummaries[provinceId].count += 1;
          provinceSummaries[provinceId].total += fsaValues[fsaName];
        });
      });

      Object.keys(provinceSummaries).forEach((provinceId) => {
        const summary = provinceSummaries[provinceId];
        summary.value = summary.count > 0 ? summary.total / summary.count : 0;
      });

      return provinceSummaries;
    },
    getMaxProvinceClusterValue() {
      const values = Object.values(this.provinceSummaryByPruid)
        .map((provinceSummary) => Number(provinceSummary.value))
        .filter((value) => Number.isFinite(value));
      return values.length > 0 ? Math.max(...values, 1) : 1;
    },
    getProvinceClusterStyle(feature, provinceSummaries, maxValue) {
      const provinceId = String(feature.properties?.PRUID || "");
      const provinceSummary = provinceSummaries[provinceId];
      const hasValue = Boolean(provinceSummary);
      const fillColor = hasValue
        ? this.getHeatmapColor(provinceSummary.value, maxValue)
        : "#F4F6F8";

      return {
        color: hasValue ? "#263238" : "#8A9490",
        fillColor,
        fillOpacity: hasValue ? 0.58 : 0.12,
        lineCap: "round",
        lineJoin: "round",
        opacity: hasValue ? 0.78 : 0.38,
        weight: hasValue ? 0.85 : 0.45,
      };
    },
    getProvinceName(feature) {
      return (
        feature.properties?.PRENAME ||
        feature.properties?.PRNAME ||
        feature.properties?.name ||
        "Province"
      );
    },
    renderFsaLayer(geoJsonList, shouldFitBounds = true) {
      if (!this.map || !this.fsaLayer || !window.L) {
        return;
      }

      this.clearFsaLayer();

      const fsaValues = this.getFsaValueMap();
      const maxValue = this.getMaxProvinceValue();
      const dataBounds = window.L.latLngBounds();
      const geoJsonItems = Array.isArray(geoJsonList) ? geoJsonList : [geoJsonList];
      const detailSettings = this.getRenderDetailSettings();
      const dataFsaNames = this.getFsaNameSet();

      geoJsonItems.forEach((geoJson) => {
        window.L.geoJSON(geoJson, {
          filter: (feature) => {
            if (!detailSettings.showOnlyDataFsa) {
              return true;
            }

            const fsaName = String(feature.properties?.name || "").toUpperCase();
            return dataFsaNames.has(fsaName);
          },
          interactive: this.renderDetailMode !== "low",
          renderer: this.vectorRenderer,
          smoothFactor: detailSettings.smoothFactor,
          style: (feature) => this.getFsaStyle(feature, fsaValues, maxValue),
          onEachFeature: (feature, layer) => {
            const fsaName = feature.properties?.name || "FSA";
            const hasValue = Object.prototype.hasOwnProperty.call(fsaValues, fsaName.toUpperCase());
            const value = hasValue ? fsaValues[fsaName.toUpperCase()] : 0;

            if (hasValue && typeof layer.getBounds === "function") {
              dataBounds.extend(layer.getBounds());
            }

            if (this.renderDetailMode === "low") {
              return;
            }

            layer.bindTooltip(
              `${this.escapeHtml(fsaName)}<br/>${this.getSelectedStatLabel()}: ${this.formatStatValue(value)}`,
              {
              direction: "auto",
              opacity: 0.86,
              sticky: true,
              }
            );

            layer.on({
              mouseover: () => {
                layer.setStyle({
                  color: "#111827",
                  fillOpacity: hasValue ? 0.82 : 0.2,
                  weight: 1.4,
                });
              },
              mouseout: () => {
                layer.setStyle(this.getFsaStyle(feature, fsaValues, maxValue));
              },
            });
          },
        }).addTo(this.fsaLayer);
      });

      if (this.renderDetailMode === "low") {
        this.setupLowDetailTooltip(geoJsonItems, fsaValues, dataFsaNames);
      }

      if (shouldFitBounds) {
        this.fitMapToDataBounds(dataBounds);
      }
    },
    getFsaNameSet() {
      return new Set(
        (this.chart_data || [])
          .filter((item) => item && typeof item.name === "string" && item.name.length > 0)
          .map((item) => item.name.toUpperCase())
      );
    },
    getFsaValueMap() {
      return this.province_data.reduce((values, item) => {
        const fsaName = String(item.name || "").toUpperCase();
        const value = Number(item.value);
        values[fsaName] = Number.isFinite(value) ? value : 0;
        return values;
      }, {});
    },
    getMaxProvinceValue() {
      const values = this.province_data
        .map((item) => Number(item.value))
        .filter((value) => Number.isFinite(value));
      return values.length > 0 ? Math.max(...values, 1) : 1;
    },
    getFsaStyle(feature, fsaValues, maxValue) {
      const fsaName = String(feature.properties?.name || "").toUpperCase();
      const hasValue = Object.prototype.hasOwnProperty.call(fsaValues, fsaName);
      const value = hasValue ? fsaValues[fsaName] : 0;
      const isLowDetailMode = this.renderDetailMode === "low";
      const fillColor = hasValue ? this.getHeatmapColor(value, maxValue) : "#F4F6F8";

      return {
        color: hasValue ? (isLowDetailMode ? fillColor : "#263238") : "#8A9490",
        fillColor,
        fillOpacity: hasValue ? (isLowDetailMode ? 0.6 : 0.7) : 0.18,
        lineCap: "round",
        lineJoin: "round",
        opacity: hasValue ? (isLowDetailMode ? 0.72 : 0.75) : 0.42,
        weight: hasValue ? (isLowDetailMode ? 0.95 : 0.75) : 0.45,
      };
    },
    getHeatmapColor(value, maxValue) {
      if (!Number.isFinite(value) || value <= 0) {
        return this.heatmap_colors_red_blue[0];
      }

      const colorIndex = Math.min(
        this.heatmap_colors_red_blue.length - 1,
        Math.round((value / Math.max(maxValue, 1)) * (this.heatmap_colors_red_blue.length - 1))
      );
      return this.heatmap_colors_red_blue[colorIndex];
    },
    clearFsaLayer() {
      this.clearLowDetailTooltipHandlers();
      if (this.fsaLayer) {
        this.fsaLayer.clearLayers();
      }
    },
    clearProvinceClusterLayer() {
      if (this.provinceClusterLayer) {
        this.provinceClusterLayer.clearLayers();
      }
    },
    clearMapLayers() {
      this.clearFsaLayer();
      this.clearProvinceClusterLayer();
      this.fsaGeoJsonItems = [];
      this.canadaClusterDataByMode = {};
      this.provinceSummaryByPruid = {};
      this.activeMapMode = null;
    },
    invalidateMapSize() {
      if (!this.map) {
        return;
      }

      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      });
    },
    setupLowDetailTooltip(geoJsonItems, fsaValues, dataFsaNames) {
      this.clearLowDetailTooltipHandlers();

      if (!this.map || this.isClusterMapMode() || !window.L) {
        return;
      }

      this.lowDetailTooltipRecords = this.buildLowDetailTooltipRecords(
        geoJsonItems,
        fsaValues,
        dataFsaNames
      );

      if (this.lowDetailTooltipRecords.length === 0) {
        return;
      }

      this.lowDetailTooltip = window.L.tooltip({
        direction: "auto",
        opacity: 0.86,
        sticky: true,
      });

      this.map.on("mousemove", this.handleLowDetailTooltipMouseMove);
      this.map.on("mouseout", this.hideLowDetailTooltip);
    },
    clearLowDetailTooltipHandlers() {
      if (this.map) {
        this.map.off("mousemove", this.handleLowDetailTooltipMouseMove);
        this.map.off("mouseout", this.hideLowDetailTooltip);
      }

      if (this.lowDetailTooltipFrame) {
        window.cancelAnimationFrame(this.lowDetailTooltipFrame);
      }

      this.hideLowDetailTooltip();
      this.lowDetailTooltip = null;
      this.lowDetailTooltipRecords = [];
      this.lowDetailTooltipFrame = null;
      this.lowDetailTooltipEvent = null;
      this.lowDetailHoveredFsaName = null;
    },
    handleLowDetailTooltipMouseMove(event) {
      if (this.isClusterMapMode()) {
        return;
      }

      this.lowDetailTooltipEvent = event;

      if (this.lowDetailTooltipFrame) {
        return;
      }

      this.lowDetailTooltipFrame = window.requestAnimationFrame(() => {
        this.lowDetailTooltipFrame = null;
        this.updateLowDetailTooltip(this.lowDetailTooltipEvent);
      });
    },
    updateLowDetailTooltip(event) {
      if (
        this.isClusterMapMode() ||
        !this.map ||
        !this.lowDetailTooltip ||
        !event ||
        !event.latlng
      ) {
        return;
      }

      const hoveredRecord = this.findLowDetailTooltipRecord(event.latlng);
      if (!hoveredRecord) {
        this.hideLowDetailTooltip();
        return;
      }

      if (this.lowDetailHoveredFsaName !== hoveredRecord.name) {
        this.lowDetailTooltip.setContent(
          `${this.escapeHtml(hoveredRecord.name)}<br/>${this.getSelectedStatLabel()}: ${this.formatStatValue(hoveredRecord.value)}`
        );
        this.lowDetailHoveredFsaName = hoveredRecord.name;
      }

      this.lowDetailTooltip.setLatLng(event.latlng);
      if (!this.map.hasLayer(this.lowDetailTooltip)) {
        this.lowDetailTooltip.addTo(this.map);
      }
    },
    hideLowDetailTooltip() {
      if (this.map && this.lowDetailTooltip && this.map.hasLayer(this.lowDetailTooltip)) {
        this.map.removeLayer(this.lowDetailTooltip);
      }
      this.lowDetailHoveredFsaName = null;
    },
    buildLowDetailTooltipRecords(geoJsonItems, fsaValues, dataFsaNames) {
      const records = [];

      geoJsonItems.forEach((geoJson) => {
        (geoJson.features || []).forEach((feature) => {
          const fsaName = String(feature.properties?.name || "").toUpperCase();
          if (!dataFsaNames.has(fsaName)) {
            return;
          }

          const polygons = this.getTooltipPolygons(feature.geometry);
          if (polygons.length === 0) {
            return;
          }

          records.push({
            bounds: this.getTooltipPolygonBounds(polygons),
            name: fsaName,
            polygons,
            value: fsaValues[fsaName] || 0,
          });
        });
      });

      return records;
    },
    getTooltipPolygons(geometry) {
      if (!geometry || !Array.isArray(geometry.coordinates)) {
        return [];
      }

      if (geometry.type === "Polygon") {
        return [geometry.coordinates];
      }

      if (geometry.type === "MultiPolygon") {
        return geometry.coordinates;
      }

      return [];
    },
    getTooltipPolygonBounds(polygons) {
      const bounds = {
        maxLat: -Infinity,
        maxLng: -Infinity,
        minLat: Infinity,
        minLng: Infinity,
      };

      polygons.forEach((polygon) => {
        polygon.forEach((ring) => {
          ring.forEach((position) => {
            const lng = position[0];
            const lat = position[1];
            bounds.minLat = Math.min(bounds.minLat, lat);
            bounds.maxLat = Math.max(bounds.maxLat, lat);
            bounds.minLng = Math.min(bounds.minLng, lng);
            bounds.maxLng = Math.max(bounds.maxLng, lng);
          });
        });
      });

      return bounds;
    },
    findLowDetailTooltipRecord(latlng) {
      const lat = latlng.lat;
      const lng = latlng.lng;

      for (let index = 0; index < this.lowDetailTooltipRecords.length; index += 1) {
        const record = this.lowDetailTooltipRecords[index];
        if (
          lat < record.bounds.minLat ||
          lat > record.bounds.maxLat ||
          lng < record.bounds.minLng ||
          lng > record.bounds.maxLng
        ) {
          continue;
        }

        if (this.isPointInTooltipPolygons(lng, lat, record.polygons)) {
          return record;
        }
      }

      return null;
    },
    isPointInTooltipPolygons(lng, lat, polygons) {
      return polygons.some((polygon) => {
        if (!this.isPointInTooltipRing(lng, lat, polygon[0])) {
          return false;
        }

        return !polygon.slice(1).some((ring) => this.isPointInTooltipRing(lng, lat, ring));
      });
    },
    isPointInTooltipRing(lng, lat, ring) {
      if (!Array.isArray(ring) || ring.length < 4) {
        return false;
      }

      let isInside = false;
      for (let index = 0, previousIndex = ring.length - 1; index < ring.length; previousIndex = index, index += 1) {
        const current = ring[index];
        const previous = ring[previousIndex];
        const currentLng = current[0];
        const currentLat = current[1];
        const previousLng = previous[0];
        const previousLat = previous[1];
        const crossesLatitude = currentLat > lat !== previousLat > lat;
        if (!crossesLatitude) {
          continue;
        }

        const intersectionLng =
          ((previousLng - currentLng) * (lat - currentLat)) / (previousLat - currentLat) +
          currentLng;
        if (lng < intersectionLng) {
          isInside = !isInside;
        }
      }

      return isInside;
    },
    getDataBoundsFromFsaGeoJsonItems(geoJsonList) {
      const bounds = window.L.latLngBounds();
      const dataFsaNames = this.getFsaNameSet();
      const geoJsonItems = Array.isArray(geoJsonList) ? geoJsonList : [geoJsonList];

      geoJsonItems.forEach((geoJson) => {
        (geoJson.features || []).forEach((feature) => {
          const fsaName = String(feature.properties?.name || "").toUpperCase();
          if (!dataFsaNames.has(fsaName)) {
            return;
          }

          this.extendBoundsWithGeometry(bounds, feature.geometry);
        });
      });

      return bounds;
    },
    extendBoundsWithGeometry(bounds, geometry) {
      if (!bounds || !geometry || !Array.isArray(geometry.coordinates)) {
        return;
      }

      this.extendBoundsWithCoordinates(bounds, geometry.coordinates);
    },
    extendBoundsWithCoordinates(bounds, coordinates) {
      if (!Array.isArray(coordinates)) {
        return;
      }

      if (
        coordinates.length >= 2 &&
        typeof coordinates[0] === "number" &&
        typeof coordinates[1] === "number"
      ) {
        const lng = coordinates[0];
        const lat = coordinates[1];
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          bounds.extend([lat, lng]);
        }
        return;
      }

      coordinates.forEach((coordinate) => this.extendBoundsWithCoordinates(bounds, coordinate));
    },
    fitMapToFsaLayer() {
      if (!this.map || !this.fsaLayer || this.fsaLayer.getLayers().length === 0) {
        this.fitMapToCanada();
        return;
      }

      const bounds = this.fsaLayer.getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(bounds.pad(0.05));
      }
    },
    fitMapToDataBounds(bounds) {
      if (this.map && bounds && bounds.isValid()) {
        this.map.fitBounds(bounds.pad(0.12));
        return;
      }

      this.fitMapToFsaLayer();
    },
    fitMapToCanada() {
      if (this.map) {
        this.map.fitBounds(CANADA_BOUNDS);
      }
    },
    formatPercentage(value) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return "0%";
      }

      if (numericValue > 0.01) {
        return `${numericValue.toFixed(2)}%`;
      }

      if (numericValue > 0.00001) {
        return `${numericValue.toFixed(5)}%`;
      }

      return `${numericValue}%`;
    },
    formatStatValue(value) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return this.getSelectedStatKey() === "reach" ? "0%" : "0";
      }

      if (this.getSelectedStatKey() === "reach") {
        return this.formatPercentage(numericValue);
      }

      if (this.getSelectedStatKey() === "frequency") {
        return numericValue.toFixed(2);
      }

      return Intl.NumberFormat("en", {
        maximumFractionDigits: 0,
        notation: "compact",
      }).format(numericValue);
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
  },
};
</script>

<style scoped>
.geomap {
  position: relative;
}

.chart_style {
  font-family: "Poppins" !important;
  height: 68vh;
}

.geomap-title {
  font-family: "Poppins" !important;
  font-size: 18px;
  font-weight: 600;
  height: 35px;
  text-align: center;
}

.geomap-chart-body {
  display: block;
  height: 100%;
  isolation: isolate;
  position: relative;
  width: 100%;
  z-index: 0;
}

.geomap-map {
  background: #d8dad8;
  height: 100%;
  position: relative;
  width: 100%;
  z-index: 0;
}

.geomap-loader {
  background: rgba(255, 255, 255, 0.72);
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 500;
}

.geomap-legend {
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(17, 24, 39, 0.18);
  border-radius: 4px;
  bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  color: #111827;
  display: flex;
  font-family: "Poppins" !important;
  font-size: 11px;
  gap: 8px;
  padding: 6px 8px;
  position: absolute;
  right: 16px;
  z-index: 450;
}

.geomap-legend__gradient {
  background: linear-gradient(90deg, #0000ff 0%, #7f007f 50%, #ff0000 100%);
  display: inline-block;
  height: 8px;
  width: 72px;
}
</style>
